import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTOR_CONFIG = {
  healthcare:         { label: 'Healthcare',        color: '#008C86' },
  government:         { label: 'Government',         color: '#D9A245' },
  'business-finance': { label: 'Business & Finance', color: '#081D30' },
  education:          { label: 'Education',          color: '#7B6E9C' },
  'technology-ai':    { label: 'Technology & AI',    color: '#1B6B72' },
  'workforce-labor':  { label: 'Workforce & Labor',  color: '#B45309' },
};

function getCountdownDisplay(expiresAt) {
  if (!expiresAt) return { type: 'inlastcall', text: 'In Last Call' };
  const secondsLeft = Math.max(0, (new Date(expiresAt).getTime() - Date.now()) / 1000);
  const hoursLeft = secondsLeft / 3600;
  if (secondsLeft <= 0) return { type: 'inlastcall', text: 'Archiving' };
  if (hoursLeft > 24) return { type: 'inlastcall', text: 'In Last Call' };
  if (hoursLeft >= 1) {
    const h = Math.floor(hoursLeft);
    const m = Math.floor((secondsLeft % 3600) / 60);
    return { type: 'countdown', text: `${h}h ${m}m` };
  }
  return { type: 'countdown', text: `${Math.floor(secondsLeft / 60)}m` };
}

export const LastCallWidget = () => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], page: 1, total_pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const fetchPage = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/homepage/last-call?page=${p}`);
      const json = await res.json();
      setData(json);
    } catch {
      setData({ items: [], page: 1, total_pages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(page); }, [page, fetchPage]);

  // Tick every minute to refresh countdowns
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  const goTo = (p) => {
    if (p < 1 || p > data.total_pages) return;
    setPage(p);
  };

  return (
    <section className="op-sb-widget" data-testid="last-call-widget">
      {/* Header */}
      <div className="op-sb-head">
        <div className="op-sb-head-left">
          <h2 className="op-sb-title">Last Call Sector Briefs</h2>
          <p className="op-sb-subtext">These sector briefs are being archived soon.</p>
        </div>
        <span className="op-sb-sort-label">Sort: Leaving Soon →</span>
      </div>

      {/* List */}
      <div className="op-sb-list" data-testid="last-call-list">
        {loading ? (
          <SkeletonRows count={8} />
        ) : data.items.length === 0 ? (
          <div className="op-sb-empty" data-testid="last-call-empty">
            No articles in the last-call window (5–9 days old) right now.
          </div>
        ) : (
          data.items.map((item, idx) => {
            const rank = (page - 1) * 8 + idx + 1;
            const sector = SECTOR_CONFIG[item.primary_pillar] || { label: item.primary_pillar || 'General', color: '#6E716D' };
            const slug = item.slug ? `/article/${item.slug}` : '#';
            const countdown = getCountdownDisplay(item.expires_at);
            return (
              <Link
                key={item.id || idx}
                to={slug}
                className="op-sb-item"
                data-testid={`lc-item-${rank}`}
              >
                {/* Thumbnail flush left */}
                {item.featured_image_url ? (
                  <img
                    src={item.featured_image_url}
                    alt=""
                    className="op-sb-thumb"
                    loading="lazy"
                  />
                ) : (
                  <div className="op-sb-thumb-placeholder">
                    <Clock size={18} strokeWidth={1.5} style={{ color: '#D8D0C0' }} />
                  </div>
                )}

                {/* Rank */}
                <span className="op-sb-rank">{String(rank).padStart(2, '0')}</span>

                {/* Sector + Title */}
                <div className="op-sb-meta">
                  <div className="op-sb-sector" style={{ color: sector.color }}>
                    {sector.label}
                  </div>
                  <div className="op-sb-article-title">{item.title}</div>
                </div>

                {/* Countdown badge */}
                <div className="op-sb-right">
                  <span
                    className={`op-lc-badge ${countdown.type === 'inlastcall' ? 'op-lc-badge--static' : 'op-lc-badge--countdown'}`}
                    data-testid={`lc-badge-${rank}`}
                  >
                    {countdown.text}
                  </span>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {!loading && data.total > 0 && (
        <div className="op-sb-pagination" data-testid="lc-pagination">
          <button
            className="op-sb-page-btn"
            onClick={() => goTo(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
            data-testid="lc-prev-btn"
          >
            <ChevronLeft size={14} strokeWidth={2} />
          </button>
          <span className="op-sb-page-label" data-testid="lc-page-label">
            Page {data.page} of {data.total_pages}
          </span>
          <button
            className="op-sb-page-btn"
            onClick={() => goTo(page + 1)}
            disabled={page >= data.total_pages}
            aria-label="Next page"
            data-testid="lc-next-btn"
          >
            <ChevronRight size={14} strokeWidth={2} />
          </button>
        </div>
      )}
    </section>
  );
};

const SkeletonRows = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="op-sb-item op-sb-item--skeleton">
        <div className="op-sb-thumb-placeholder" style={{ background: '#e8e4dc' }} />
        <span className="op-sb-rank" style={{ color: 'transparent', background: '#e8e4dc', borderRadius: 3 }}>00</span>
        <div className="op-sb-meta">
          <div style={{ height: 9, width: '38%', background: '#e8e4dc', borderRadius: 3 }} />
          <div style={{ height: 12, width: '78%', background: '#e8e4dc', borderRadius: 3 }} />
          <div style={{ height: 12, width: '55%', background: '#e8e4dc', borderRadius: 3 }} />
        </div>
        <div className="op-sb-right">
          <div style={{ width: 60, height: 22, background: '#e8e4dc', borderRadius: 5 }} />
        </div>
      </div>
    ))}
  </>
);
